// 組織與場域 › 〈家戶〉› 監控設定（`/sites/kmgr/:home`，SPEC #kinio-kms-ui「設定只有兩個家」，kmgr.configure）。
// M1：佈署權杖（建立、只顯示一次、報到進度、撤銷）與通知（選輪值表 + 客戶聯絡人，M1-11 的 PUT …/notify）。
// M2-13：收集器、探針、門檻與等級、維護與營業時段、現場資訊（HomeMonitoring.tsx）；自動處置分頁跟著 M3 加。
import { useState } from 'react';
import { Link, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { usePageSubject } from '../../lib/pageTitle';
import type { MyOrg } from '../../lib/planeApi';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { kmgrAdminOrgs, useHomeApi, useHomeKmgrStatus, useKmgrEnv } from './hooks';
import { Crumbs, errText, NS, StateBox } from './parts';
import { DeployTokenForm, DeployTokenList, TokenOnce, TokenProgress } from './DeployTokens';
import { memberName, useOrgMembers, useSchedules } from './Oncall';
import type { CreatedDeployToken } from './types';
import { CollectorsTab, ProbesTab, SiteInfoTab, ThresholdsTab, WindowsTab } from './HomeMonitoring';
import { HomeTemplateBanner } from './templates/HomeTemplateBanner';

const TABS = ['tokens', 'collectors', 'thresholds', 'probes', 'windows', 'notify', 'site'] as const;
type Tab = (typeof TABS)[number];

export function HomeSettingsPage() {
  const { t } = useTranslation(NS);
  useKmgrEnv();
  const { homeId = '' } = useParams();
  const { home, api } = useHomeApi(homeId);
  const { orgs } = useScope();
  const loc = useLocation();
  const org = orgs.find((o) => o.org_id === home?.org_id);
  // 能建範本的組織：家戶所屬組織（我是 owner / org_admin）優先，否則我管的第一個 team 組織（安裝商經委派管客戶家戶）；要同一個 plane
  const tplOrgs = kmgrAdminOrgs(orgs).filter((o) => o.kind === 'team' && (!api || o.api === api));
  const tplOrg = tplOrgs.find((o) => o.org_id === home?.org_id) ?? tplOrgs[0];
  const base = `/sites/kmgr/${encodeURIComponent(homeId)}`;
  const rest = loc.pathname.slice(base.length).replace(/^\//, '');
  const tab: Tab = (TABS.find((k) => k !== 'tokens' && rest.startsWith(k)) ?? 'tokens') as Tab;
  const tokenId = /^tokens\/([^/]+)$/.exec(rest)?.[1];
  const settings = useHomeKmgrStatus(api, homeId);
  const tokName = useQuery({
    queryKey: ['kmgr', 'token', homeId, tokenId],
    queryFn: () => kmgrApi.deployToken(api ?? '', homeId, decodeURIComponent(tokenId ?? '')),
    enabled: !!api && !!tokenId && tokenId !== 'new',
  });
  usePageSubject(home?.display_name);
  if (!home || !api)
    return (
      <section className="page km">
        <StateBox icon="⊘" title={t('home_settings.not_found')}>
          <Link className="btn" to="/sites">
            {t('home_settings.back_sites')}
          </Link>
        </StateBox>
      </section>
    );
  const crumbs = [{ label: t('home_settings.crumb_sites'), to: '/sites' }, ...(org ? [{ label: org.display_name }] : []), { label: home.display_name ?? homeId }, { label: t('home_settings.title'), to: base }];
  if (tab === 'tokens' && rest.startsWith('tokens')) crumbs.push({ label: t('home_settings.tab_tokens'), to: base });
  if (rest === 'tokens/new') crumbs.push({ label: t('token.new_title') });
  else if (tokenId) crumbs.push({ label: tokName.data?.name ?? '…' });
  const detail = rest.startsWith('tokens/');
  return (
    <section className="page km" data-testid="km-home-settings">
      <Crumbs items={crumbs} />
      {!detail && <h1 className="page-title">{t('home_settings.title')}</h1>}
      {settings.data && !settings.data.kmgr_enabled && (
        <div className="km-bar warn" role="status">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">{t('home_settings.not_enabled')}</span>
          <Link className="btn btn-sm" to="/org/kmgr/enable">
            {t('org.start_wizard')}
          </Link>
        </div>
      )}
      {!detail && <HomeTemplateBanner api={api} homeId={homeId} />}
      {!detail && settings.data?.kmgr_enabled && tplOrg && (
        <div className="km-block-head" style={{ marginBottom: 8 }}>
          <span className="spacer" />
          {/* 〔套用到其他家戶…〕：用這個家戶目前的設定建範本，再走批次套用（SPEC #kinio-kms-templates「批次套用」） */}
          <Link className="btn btn-sm" to={`/org/kmgr/templates/new?org=${encodeURIComponent(tplOrg.org_id)}&from_home=${encodeURIComponent(homeId)}`} data-testid="km-home-apply-others">
            {t('tpl.apply_to_others')}
          </Link>
        </div>
      )}
      {!detail && (
        <nav className="km-tabs" aria-label={t('home_settings.tabs')}>
          {TABS.map((k) => (
            <Link key={k} to={k === 'tokens' ? base : `${base}/${k}`} aria-current={tab === k ? 'page' : undefined} data-tab={k}>
              {t(`home_settings.tab_${k}`)}
            </Link>
          ))}
        </nav>
      )}
      <Routes>
        <Route
          index
          element={
            <>
              <div className="km-block-head">
                <p className="muted" style={{ margin: 0 }}>
                  {t('home_settings.tokens_intro')}
                </p>
                <span className="spacer" />
                <Link className="btn btn-primary" to={base + '/tokens/new'}>
                  {t('token.new')}
                </Link>
              </div>
              <DeployTokenList api={api} homeId={homeId} />
            </>
          }
        />
        <Route path="tokens/new" element={<NewToken api={api} homeId={homeId} homeLabel={[org?.display_name, home.display_name].filter(Boolean).join(' › ')} />} />
        <Route path="tokens/:tokenId" element={<TokenProgressRoute api={api} homeId={homeId} />} />
        <Route path="notify" element={org ? <NotifySettings org={org} homeId={homeId} api={api} /> : <p className="muted">{t('home_settings.no_org')}</p>} />
        <Route path="collectors" element={<CollectorsTab api={api} homeId={homeId} org={org} />} />
        <Route path="probes" element={<ProbesTab api={api} homeId={homeId} />} />
        <Route path="thresholds" element={<ThresholdsTab api={api} homeId={homeId} />} />
        <Route path="windows" element={<WindowsTab api={api} homeId={homeId} />} />
        <Route path="site" element={<SiteInfoTab api={api} homeId={homeId} orgName={org?.display_name} />} />
      </Routes>
    </section>
  );
}

function TokenProgressRoute({ api, homeId }: { api: string; homeId: string }) {
  const { tokenId = '' } = useParams();
  return <TokenProgress api={api} homeId={homeId} tokenId={tokenId} />;
}

function NewToken({ api, homeId, homeLabel }: { api: string; homeId: string; homeLabel: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const [created, setCreated] = useState<CreatedDeployToken | null>(null);
  if (created) return <TokenOnce token={created} api={api} homeLabel={homeLabel} />;
  return (
    <>
      <h1 className="page-title">{t('token.new_title')}</h1>
      <section className="card">
        <DeployTokenForm
          api={api}
          homes={[{ home_id: homeId, label: homeLabel }]}
          homeId={homeId}
          defaultName={t('token.default_name', { home: homeLabel.split(' › ').pop() })}
          onCreated={(c) => (setCreated(c), void qc.invalidateQueries({ queryKey: ['kmgr', 'tokens', homeId] }))}
        />
      </section>
    </>
  );
}

/** 監控設定 › 通知：選輪值表 + 客戶聯絡人（PUT …/notify，step-up；成功時 kmgr 通知家戶所屬組織）。 */
function NotifySettings({ org, homeId, api }: { org: MyOrg; homeId: string; api: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['kmgr', 'notify', homeId], queryFn: () => kmgrApi.notify(api, homeId), retry: 1 });
  const schedules = useSchedules(org);
  const members = useOrgMembers(org);
  const [schedule, setSchedule] = useState<string | null | undefined>(undefined);
  const [contact, setContact] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (settings.isLoading || schedules.isLoading) return <SkeletonLines n={3} label={t('common.loading')} />;
  if (settings.isError)
    return (
      <StateBox icon="⊘" title={t('home_settings.unavailable')} body={errText(t, settings.error)}>
        <button className="btn" onClick={() => void settings.refetch()}>
          {t('common.retry')}
        </button>
      </StateBox>
    );
  const cur = settings.data;
  const sch = schedule === undefined ? (cur?.schedule_id ?? null) : schedule;
  const con = contact === undefined ? (cur?.customer_contact ?? null) : contact;
  const owner = members.data?.find((m) => m.role === 'owner');
  const dirty = sch !== (cur?.schedule_id ?? null) || con !== (cur?.customer_contact ?? null);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await kmgrApi.setNotify(api, homeId, { schedule_id: sch, customer_contact: con });
      await qc.invalidateQueries({ queryKey: ['kmgr', 'notify', homeId] });
      setSchedule(undefined);
      setContact(undefined);
      setMsg({ ok: true, text: t('notify.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card" data-testid="km-notify">
      <div className="km-form">
        <label className="field">
          <span>{t('notify.schedule')}</span>
          <select className="input" value={sch ?? ''} onChange={(e) => setSchedule(e.target.value || null)}>
            <option value="">{t('notify.no_schedule')}</option>
            {(schedules.data ?? []).map((s) => (
              <option key={s.schedule_id} value={s.schedule_id}>
                {s.name}
              </option>
            ))}
          </select>
          <span className="hint">{sch ? t('notify.schedule_hint') : t('notify.no_schedule_hint')}</span>
        </label>
        <label className="field">
          <span>{t('notify.contact')}</span>
          <select className="input" value={con ?? ''} onChange={(e) => setContact(e.target.value || null)}>
            <option value="">{owner ? t('notify.contact_default', { name: memberName(members.data, owner.user_id) }) : t('notify.contact_default_none')}</option>
            {(members.data ?? []).map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {memberName(members.data, m.user_id)}
              </option>
            ))}
          </select>
          <span className="hint">{t('notify.contact_hint')}</span>
        </label>
      </div>
      <p className="muted km-sub">{t('notify.channels')}</p>
      {msg && (
        <p className={msg.ok ? 'notice' : 'error'} role="status">
          {msg.text}
        </p>
      )}
      <div className="km-dock">
        <span className="why">{t('notify.save_note')}</span>
        <button className="btn btn-primary" disabled={!dirty || busy} onClick={() => void save()}>
          {busy ? t('common.saving') : t('notify.save')}
        </button>
      </div>
      <p className="km-sub">
        <Link to="/org/kmgr/oncall">{t('notify.edit_schedules')}</Link>
      </p>
    </section>
  );
}
